import type { FormInstance } from 'antd';

export type ApiFieldValidationError = {
  field?: string;
  message?: string;
  detail?: string;
};

function asArray(value: unknown): any[] {
  return Array.isArray(value) ? value : [];
}

function normalizeMessage(value: any): string {
  if (typeof value?.message === 'string' && value.message.trim())
    return value.message.trim();
  if (typeof value?.detail === 'string' && value.detail.trim())
    return value.detail.trim();
  return 'Invalid value';
}

function normalizeField(value: any): string {
  if (typeof value?.field === 'string' && value.field.trim())
    return value.field.trim();
  if (typeof value?.name === 'string' && value.name.trim())
    return value.name.trim();
  return '';
}

function toNamePath(field: string): (string | number)[] {
  return field
    .replace(/\[(\d+)]/g, '.$1')
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => (/^\d+$/.test(part) ? Number(part) : part));
}

function readFieldErrors(error: any): any[] {
  return [
    ...asArray(error?.response?.data?.errors),
    ...asArray(error?.data?.errors),
    ...asArray(error?.fieldErrors),
    ...asArray(error?.info?.data?.errors),
  ];
}

export function extractApiFieldErrors(
  error: any,
): Array<{ field: string; message: string }> {
  const rawErrors = readFieldErrors(error);
  if (!rawErrors.length) return [];

  const seen = new Set<string>();
  const normalized: Array<{ field: string; message: string }> = [];

  rawErrors.forEach((item) => {
    const field = normalizeField(item);
    const message = normalizeMessage(item);
    const key = `${field}::${message}`;
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push({ field, message });
  });

  return normalized;
}

export function formatApiValidationMessage(error: any): string | undefined {
  const errors = extractApiFieldErrors(error);
  if (!errors.length) return undefined;

  const items = errors
    .slice(0, 3)
    .map((item) =>
      item.field ? `${item.field}: ${item.message}` : item.message,
    );
  const suffix = errors.length > 3 ? ` (+${errors.length - 3} more)` : '';
  return `${items.join('; ')}${suffix}`;
}

export function applyApiFieldErrorsToForm(
  form: FormInstance<any>,
  error: any,
  fieldMap?: Record<string, string>,
): boolean {
  const errors = extractApiFieldErrors(error);
  if (!errors.length) return false;

  const roots = new Set(
    form
      .getFieldsError()
      .map((item) => item.name?.[0])
      .filter((name): name is string => typeof name === 'string' && !!name),
  );

  const mapped = errors
    .map((item) => {
      const mappedField = fieldMap?.[item.field] ?? item.field;
      if (!mappedField) return null;

      const path = toNamePath(mappedField);
      if (!path.length) return null;

      const first = path[0];
      if (roots.size > 0 && typeof first === 'string' && !roots.has(first)) {
        const fallback = path[path.length - 1];
        if (typeof fallback === 'string' && roots.has(fallback)) {
          return { name: [fallback], errors: [item.message] };
        }
        return null;
      }

      return { name: path, errors: [item.message] };
    })
    .filter(
      (item): item is { name: (string | number)[]; errors: string[] } => !!item,
    );

  if (!mapped.length) return false;
  form.setFields(mapped);
  form.scrollToField(mapped[0].name);
  return true;
}
